import { AuthenticationDetails, CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';
import { ActionCreator } from 'app/redux/ActionCreator';
import { ActionTypes, IUserAction, IUserRegistrationResult, IUserState } from 'app/redux/User/reducer';

function getAuthErrorCode(error: any): string {
  if (!error) {
    return ''
  }

  if (typeof error === 'string') {
    return error
  }

  return error.code || error.name || ''
}

function getAuthErrorMessage(error: any): string {
  const code = getAuthErrorCode(error)

  if (typeof error === 'string' && code === 'Incorrect login credentials') {
    return error
  }

  switch (code) {
    case 'CodeMismatchException':
      return 'The confirmation code is incorrect. Please check the code and try again.'
    case 'ExpiredCodeException':
      return 'The confirmation code has expired. Please request a new verification code.'
    case 'UserNotFoundException':
      return 'We could not find an account for that username or email.'
    case 'NotAuthorizedException':
      return 'This account may already be confirmed. Please sign in.'
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts were made. Please wait a few minutes and try again.'
    case 'InvalidParameterException':
      return 'Please check the information entered and try again.'
    case 'UserNotConfirmedException':
      return 'Your account must be confirmed before you can sign in.'
    case 'NetworkError':
      return 'A network error occurred. Please check your connection and try again.'
    case 'MissingUsername':
      return 'Please enter your username or email.'
    case 'MissingConfirmationCode':
      return 'Please enter your confirmation code.'
    default:
      if (error && error.message) {
        return error.message
      }

      return 'Something went wrong. Please try again.'
  }
}

function isUserNotConfirmedError(error: any): boolean {
  return getAuthErrorCode(error) === 'UserNotConfirmedException'
}

function isAlreadyConfirmedError(error: any): boolean {
  return getAuthErrorCode(error) === 'NotAuthorizedException'
}

class UserActionCreator extends ActionCreator<IUserState, IUserAction> {
  private static GroupName = 'AuthenticatedUsers';
  private static LOCAL_STORAGE_KEY = 'U$3R';
  private static UserPoolId = 'us-east-1_PhYxWyTcQ';
  private static AppClientId = 'jih782a1vd9h13o0mjkj3odm8';

  init() {
    try {
      const userJSONStr = localStorage.getItem(UserActionCreator.LOCAL_STORAGE_KEY);
      const user = JSON.parse(userJSONStr) as IUserState;

      const data = {
        UserPoolId: UserActionCreator.UserPoolId, // Your user pool id here
        ClientId: UserActionCreator.AppClientId, // Your client id here
      };
      const userPool = new CognitoUserPool(data);
      const cognitoUser = userPool.getCurrentUser();

      if (cognitoUser != null) {
        cognitoUser.getSession((err, session) => {
          if (err) {
            localStorage.removeItem(UserActionCreator.LOCAL_STORAGE_KEY);

            this.dispatch({ type: ActionTypes.INIT });
            return;
          } else {
            this.dispatch({ type: ActionTypes.LOGIN, user });
          }
        });
      } else {
        this.dispatch({ type: ActionTypes.INIT });
      }
    } catch (noProperLoginData) {
      this.dispatch({ type: ActionTypes.INIT });
    }
  }

  login(username: string, token: string) {
    return this.dispatchAsync((getState) => {
      return new Promise((resolve, reject) => {
        const authenticationData = {
          Username: username,
          Password: token,
        };
        const authenticationDetails = new AuthenticationDetails(authenticationData);
        const poolData = {
          UserPoolId: UserActionCreator.UserPoolId, // Your user pool id here
          ClientId: UserActionCreator.AppClientId,
        };
        const userPool = new CognitoUserPool(poolData);
        const userData = {
          Username: username,
          Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess(result) {
            const user: IUserState = {
              username,
              token: result.getIdToken().getJwtToken(),
              accessToken: result.getAccessToken().getJwtToken(),
              isInit: true,
            };
            const type = ActionTypes.LOGIN;
            try {
              const userStr = JSON.stringify(user);
              localStorage.setItem(UserActionCreator.LOCAL_STORAGE_KEY, userStr);
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error('Unable to save user credentials');
            }
            resolve({ type: ActionTypes.LOGIN, user });
          },
          onFailure(err) {
            if (isUserNotConfirmedError(err)) {
              return reject(err);
            }

            // tslint:disable-next-line:no-console
            console.error('Unable to authenticate user', err);
            reject('Incorrect login credentials');
          },
        });
      });
    });
  }

  logout() {
    return this.dispatchAsync((getState) => {
      try {
        localStorage.setItem(UserActionCreator.LOCAL_STORAGE_KEY, null);

        const data = {
          UserPoolId: UserActionCreator.UserPoolId, // Your user pool id here
          ClientId: UserActionCreator.AppClientId, // Your client id here
        };
        const userPool = new CognitoUserPool(data);
        const cognitoUser = userPool.getCurrentUser();

        cognitoUser.signOut();
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.error('Unable to log user out');
      }

      return Promise.resolve({ type: ActionTypes.LOGOUT });
    });
  }

  confirm(username: string, confirmcode: string) {
    return this.dispatchAsync((getState) => {
      return new Promise((resolve, reject) => {
        if (!username) {
          return reject({ code: 'MissingUsername' });
        }

        if (!confirmcode) {
          return reject({ code: 'MissingConfirmationCode' });
        }

        try {
          const poolData = {
            UserPoolId: UserActionCreator.UserPoolId,
            ClientId: UserActionCreator.AppClientId,
          };

          const userPool = new CognitoUserPool(poolData);
          const userData = {
            Username: username,
            Pool: userPool,
            GroupName: UserActionCreator.GroupName,
          };

          const cognitoUser = new CognitoUser(userData);
          cognitoUser.confirmRegistration(confirmcode, true, (err, result) => {
            if (err) {
              return reject(err);
            }

            return resolve({ type: ActionTypes.CONFIRM });
          });
        } catch (e) {
          return reject(e);
        }
      });
    });
  }

  resendConfirmationCode(username: string) {
    return this.dispatchAsync((getState) => {
      return new Promise((resolve, reject) => {
        if (!username) {
          return reject({ code: 'MissingUsername' });
        }

        try {
          const poolData = {
            UserPoolId: UserActionCreator.UserPoolId,
            ClientId: UserActionCreator.AppClientId,
          };

          const userPool = new CognitoUserPool(poolData);
          const userData = {
            Username: username,
            Pool: userPool,
          };

          const cognitoUser = new CognitoUser(userData);
          cognitoUser.resendConfirmationCode((err, result) => {
            if (err) {
              return reject(err);
            }

            return resolve({ type: ActionTypes.CONFIRM });
          });
        } catch (e) {
          return reject(e);
        }
      });
    });
  }

  register(username: string, password: string, firstname: string, lastname: string, email: string) {
    return this.dispatchAsync((getState) => {
      return new Promise((resolve, reject) => {
        try {
          const poolData = {
            UserPoolId: UserActionCreator.UserPoolId,
            ClientId: UserActionCreator.AppClientId,
          };
          const userPool = new CognitoUserPool(poolData);

          const attributeList = [];

          const dataEmail = {
            Name: 'email',
            Value: email,
          };

          const dataFirstName = {
            Name: 'name',
            Value: firstname,
          };

          const dataLastName = {
            Name: 'family_name',
            Value: lastname,
          };

          const attributeEmail = new CognitoUserAttribute(dataEmail);
          const attributeFirstName = new CognitoUserAttribute(dataFirstName);
          const attributeLastName = new CognitoUserAttribute(dataLastName);

          attributeList.push(attributeEmail);
          attributeList.push(attributeFirstName);
          attributeList.push(attributeLastName);

          userPool.signUp(username, password, attributeList, null, (err, result) => {
            if (err) {
              return reject(err);
            } else {
              const registration: IUserRegistrationResult = {
                username,
                userConfirmed: result && result.userConfirmed === true,
                userSub: result ? (result as any).userSub : null,
              };

              return resolve({ type: ActionTypes.REGISTER, registration });
            }
          });
        } catch (e) {
          return reject(e);
        }
      });
    });
  }
}

const UserActions = new UserActionCreator();

export {
  getAuthErrorCode,
  getAuthErrorMessage,
  isAlreadyConfirmedError,
  isUserNotConfirmedError,
  UserActions,
  UserActionCreator
};



// WEBPACK FOOTER //
// ./src/app/redux/User/actions/index.ts